import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Chip, Button, TextField,
  Select, MenuItem, FormControl, InputLabel,
  CircularProgress, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Grading as GradingIcon,
  Search as SearchIcon,
  FilterAlt as FilterIcon,
  InboxOutlined as EmptyIcon,
  CheckCircle as CheckCircleIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { fetchData, putData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import DynamicTable from "../../DynamicComponent/DynamicTable";

const GRADES = ["All", "MunMazhalai", "Mazhalai", "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4", "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8"];
const STATUSES = ["All", "Pending", "Evaluated", "Late"];

const FALLBACK_ROWS = [
  { id: "1", studentName: "Aarav Kumar",   regNo: "REG001", grade: "Nilai 3", subject: "Tamil Reading",  assignmentTitle: "Reading Assignment Q1", submittedDate: "2025-05-20", status: "Pending",   marks: "", remarks: "", fileUrl: "#" },
  { id: "2", studentName: "Priya Sharma",  regNo: "REG002", grade: "Nilai 3", subject: "Tamil Writing",  assignmentTitle: "Writing Assignment Q2", submittedDate: "2025-05-21", status: "Evaluated", marks: "85", remarks: "Good work", fileUrl: "#" },
  { id: "3", studentName: "Karthik Raj",   regNo: "REG003", grade: "Nilai 4", subject: "Tamil Grammar",  assignmentTitle: "Grammar Instructions",  submittedDate: "2025-05-18", status: "Late",      marks: "", remarks: "", fileUrl: "#" },
  { id: "4", studentName: "Divya Nair",    regNo: "REG004", grade: "Nilai 4", subject: "Tamil Speaking", assignmentTitle: "Oral Exam Instructions", submittedDate: "2025-05-22", status: "Pending",   marks: "", remarks: "", fileUrl: "#" },
  { id: "5", studentName: "Sanjay Patel",  regNo: "REG005", grade: "Nilai 5", subject: "Tamil Reading",  assignmentTitle: "Homework Template",     submittedDate: "2025-05-19", status: "Evaluated", marks: "92", remarks: "Excellent", fileUrl: "#" },
  { id: "6", studentName: "Meera Iyer",    regNo: "REG006", grade: "Nilai 5", subject: "Cultural Studies", assignmentTitle: "Reading Assignment Q1", submittedDate: "2025-05-23", status: "Pending",   marks: "", remarks: "", fileUrl: "#" },
];

const TABLE_COLUMNS = [
  { label: "Student Name",       field: "studentName" },
  { label: "Reg No",             field: "regNo" },
  { label: "Grade",              field: "grade" },
  { label: "Subject",            field: "subject" },
  { label: "Assignment",         field: "assignmentTitle" },
  { label: "Submitted",          field: "submittedDate" },
  { label: "Status",             field: "statusChip" },
  { label: "File",               field: "fileCell" },
  { label: "Actions",            field: "actions" },
];

const statusStyle = {
  Pending:   { bg: "#fff3e0", color: "#e65100" },
  Evaluated: { bg: "#e8f5e9", color: "#2e7d32" },
  Late:      { bg: "#fce4ec", color: "#c62828" },
};

const AdminSubmissionReview = () => {
  const [rawData, setRawData]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [filters, setFilters]   = useState({ grade: "All", status: "All", search: "" });
  const [evalDialog, setEvalDialog] = useState({ open: false, row: null, marks: "", remarks: "" });
  const [toast, setToast]       = useState({ open: false, severity: "success", message: "" });

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.grade !== "All")   p.append("grade",  filters.grade);
    if (filters.status !== "All")  p.append("status", filters.status);
    if (filters.search)            p.append("search", filters.search);
    return p.toString();
  }, [filters]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery();
      const res = await fetchData(`teacher/submissions/list${qs ? "?" + qs : ""}`);
      const records = res?.data?.records || res?.data || [];
      setRawData(Array.isArray(records) && records.length > 0 ? records : FALLBACK_ROWS);
    } catch {
      setRawData(FALLBACK_ROWS);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = (row) => {
    if (row.fileUrl && row.fileUrl !== "#") window.open(row.fileUrl, "_blank");
    else setToast({ open: true, severity: "warning", message: "File not available" });
  };

  const openEval = (row) => {
    setEvalDialog({ open: true, row, marks: row.marks || "", remarks: row.remarks || "" });
  };

  const handleSaveEval = async () => {
    const { row, marks, remarks } = evalDialog;
    if (!marks) { setToast({ open: true, severity: "warning", message: "Please enter marks" }); return; }
    try {
      await putData(`teacher/submission/${row.id}/evaluate`, { marks, remarks, status: "Evaluated" });
      setRawData((prev) =>
        prev.map((r) => r.id === row.id ? { ...r, marks, remarks, status: "Evaluated" } : r)
      );
      setToast({ open: true, severity: "success", message: `Evaluation saved for ${row.studentName}` });
      setEvalDialog({ open: false, row: null, marks: "", remarks: "" });
    } catch {
      setToast({ open: true, severity: "error", message: "Failed to save evaluation" });
    }
  };

  const rows = rawData.map((r) => {
    const s = statusStyle[r.status] || { bg: "#f5f5f5", color: "#757575" };
    return {
      ...r,
      statusChip: (
        <Chip label={r.status || "Pending"} size="small"
          sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, fontSize: 12 }} />
      ),
      fileCell: r.fileUrl && r.fileUrl !== "#" ? (
        <Chip
          icon={<PdfIcon sx={{ fontSize: 14 }} />}
          label="View File"
          size="small"
          clickable
          onClick={() => handleDownload(r)}
          sx={{ backgroundColor: "#fce4ec", color: "#c62828", fontWeight: 500 }}
        />
      ) : <Typography sx={{ fontSize: 12, color: "#bbb" }}>—</Typography>,
      actions: (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Download">
            <IconButton size="small" onClick={() => handleDownload(r)}>
              <DownloadIcon fontSize="small" sx={{ color: "#244C77" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={r.status === "Evaluated" ? "Re-evaluate" : "Evaluate"}>
            <IconButton size="small" onClick={() => openEval(r)}>
              {r.status === "Evaluated"
                ? <CheckCircleIcon fontSize="small" sx={{ color: "#4caf50" }} />
                : <GradingIcon fontSize="small" sx={{ color: "#f57c00" }} />}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    };
  });

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={0.5}>Pages / Submission Review</Typography>
      <Typography variant="h5" fontWeight="bold" mb={3}>Student Submission Review</Typography>

      {/* Filter Bar */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: "1px solid #e0e0e0" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Grade</InputLabel>
            <Select value={filters.grade} label="Grade"
              onChange={(e) => setFilters((p) => ({ ...p, grade: e.target.value }))}>
              {GRADES.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filters.status} label="Status"
              onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </Select>
          </FormControl>

          <TextField
            size="small" label="Search student / assignment"
            value={filters.search}
            onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && loadData()}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: "#bbb", fontSize: 18, mr: 0.5 }} /> }}
            sx={{ minWidth: 240 }}
          />

          <Button
            variant="contained" startIcon={<FilterIcon />} onClick={loadData}
            sx={{ height: 40, background: "linear-gradient(135deg, #244C77 0%, #4A7BA8 100%)", textTransform: "none" }}
          >
            Apply
          </Button>
        </Box>
      </Paper>

      {/* Summary chips */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        {["Pending", "Evaluated", "Late"].map((s) => {
          const count = rawData.filter((r) => r.status === s).length;
          const st = statusStyle[s] || {};
          return (
            <Chip key={s} label={`${s}: ${count}`} size="small"
              sx={{ backgroundColor: st.bg, color: st.color, fontWeight: 600 }} />
          );
        })}
        <Chip label={`Total: ${rawData.length}`} size="small"
          sx={{ backgroundColor: "#EAF1F8", color: "#244C77", fontWeight: 600 }} />
      </Box>

      {/* Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#244C77" }} />
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <EmptyIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
          <Typography variant="body1" color="text.secondary">No submissions found.</Typography>
        </Box>
      ) : (
        <DynamicTable columns={TABLE_COLUMNS} rows={rows} />
      )}

      {/* Evaluate Dialog */}
      <Dialog open={evalDialog.open} onClose={() => setEvalDialog({ open: false, row: null, marks: "", remarks: "" })} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Evaluate Submission — {evalDialog.row?.studentName}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {evalDialog.row?.assignmentTitle} · {evalDialog.row?.grade}
          </Typography>
          <TextField
            label="Marks (out of 100)"
            type="number"
            fullWidth
            size="small"
            value={evalDialog.marks}
            onChange={(e) => setEvalDialog((p) => ({ ...p, marks: e.target.value }))}
            inputProps={{ min: 0, max: 100 }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Remarks"
            multiline
            rows={3}
            fullWidth
            size="small"
            value={evalDialog.remarks}
            onChange={(e) => setEvalDialog((p) => ({ ...p, remarks: e.target.value }))}
            placeholder="Add feedback for the student..."
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEvalDialog({ open: false, row: null, marks: "", remarks: "" })}
            sx={{ textTransform: "none", color: "#757575" }}>
            Cancel
          </Button>
          <Button
            variant="contained" onClick={handleSaveEval}
            sx={{ textTransform: "none", background: "linear-gradient(135deg, #244C77, #4A7BA8)" }}
          >
            Save Evaluation
          </Button>
        </DialogActions>
      </Dialog>

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminSubmissionReview;
